import { ChevronDownIcon, ChevronRightIcon, FarmerSalesIcon } from 'core/components/Icons'
import { Badge } from 'core/components/ui/Badge'
import React, { useMemo, useState } from 'react'
import { FpoWiseSales } from '../../../domain/entities/SalesReport'
import { SectionCard } from './SectionCard'
import { formatCurrency, formatDate } from './format'

interface FpoWiseSalesTableProps {
  data: FpoWiseSales
  loading?: boolean
}

interface FpoGroup {
  entityName: string
  totalUnits: number
  totalAmount: number
  repeatOrders: number
  transactions: FpoWiseSales['transactions']
}

const groupByFpo = (transactions: FpoWiseSales['transactions']): FpoGroup[] => {
  const groups = new Map<string, FpoGroup>()

  for (const t of transactions) {
    const existing = groups.get(t.entityName)
    if (existing) {
      existing.totalUnits += t.units
      existing.totalAmount += t.amount
      existing.repeatOrders += t.remark === 'Repeat Order' ? 1 : 0
      existing.transactions.push(t)
    } else {
      groups.set(t.entityName, {
        entityName: t.entityName,
        totalUnits: t.units,
        totalAmount: t.amount,
        repeatOrders: t.remark === 'Repeat Order' ? 1 : 0,
        transactions: [t],
      })
    }
  }

  return [...groups.values()].sort((a, b) => b.totalAmount - a.totalAmount)
}

export function FpoWiseSalesTable({ data, loading = false }: FpoWiseSalesTableProps) {
  const groups = useMemo(() => groupByFpo(data?.transactions || []), [data?.transactions])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggle = (entityName: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(entityName)) {
        next.delete(entityName)
      } else {
        next.add(entityName)
      }

      return next
    })
  }

  const grossMargin = (data?.totalAmount || 0) - (data?.cogs || 0)

  let tableBody: React.ReactNode
  if (loading) {
    const skeletonRows = ['fpo-skeleton-1', 'fpo-skeleton-2', 'fpo-skeleton-3', 'fpo-skeleton-4', 'fpo-skeleton-5']
    tableBody = skeletonRows.map((rowKey) => (
      <tr key={rowKey} className="animate-pulse">
        <td colSpan={5} className="px-6 py-4">
          <div className="h-4 bg-border rounded w-2/3" />
        </td>
      </tr>
    ))
  } else if (groups.length === 0) {
    tableBody = (
      <tr>
        <td colSpan={5} className="px-6 py-8 text-center text-text-secondary">
          <div className="flex flex-col items-center justify-center">
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-bg text-text-secondary">
              <FarmerSalesIcon size={20} className="opacity-60" />
            </div>
            <span className="text-xs font-semibold text-text-primary">No sales transactions.</span>
          </div>
        </td>
      </tr>
    )
  } else {
    tableBody = groups.map((group) => {
      const isOpen = expanded.has(group.entityName)
      return (
        <React.Fragment key={group.entityName}>
          <tr
            onClick={() => toggle(group.entityName)}
            className="cursor-pointer hover:bg-bg/50 transition-colors duration-150 ease-in-out"
          >
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <span className="text-text-secondary">
                  {isOpen ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                </span>
                <span className="font-semibold text-text-primary">{group.entityName}</span>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-text-secondary">{group.transactions.length}</td>
            <td className="px-6 py-4 whitespace-nowrap text-text-primary">{group.totalUnits}</td>
            <td className="px-6 py-4 whitespace-nowrap font-semibold text-text-primary">
              {formatCurrency(group.totalAmount)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              {group.repeatOrders > 0 ? (
                <Badge tone="secondary" variant="subtle">
                  {group.repeatOrders} Repeat {group.repeatOrders === 1 ? 'Order' : 'Orders'}
                </Badge>
              ) : (
                <span className="text-text-secondary/50">&mdash;</span>
              )}
            </td>
          </tr>

          {isOpen &&
            group.transactions.map((t) => (
              <tr key={t.id} className="bg-bg/30">
                <td className="px-6 py-3 whitespace-nowrap pl-12 text-text-secondary">{formatDate(t.date)}</td>
                <td className="px-6 py-3 whitespace-nowrap" />
                <td className="px-6 py-3 whitespace-nowrap text-text-primary">{t.units}</td>
                <td className="px-6 py-3 whitespace-nowrap text-text-primary">{formatCurrency(t.amount)}</td>
                <td className="px-6 py-3 whitespace-nowrap">
                  {t.remark ? (
                    <Badge tone="secondary" variant="subtle">
                      {t.remark}
                    </Badge>
                  ) : (
                    <span className="text-text-secondary/50">&mdash;</span>
                  )}
                </td>
              </tr>
            ))}
        </React.Fragment>
      )
    })
  }

  return (
    <SectionCard title="FPO Wise Sales" subtitle="Full transaction log across all FPOs for the reporting period">
      <div className="flex flex-col w-full bg-surface border border-border rounded-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border table-auto text-left text-sm">
            <thead className="bg-bg text-text-secondary text-xs uppercase tracking-wider font-semibold select-none">
              <tr>
                <th className="px-6 py-4">FPO</th>
                <th className="px-6 py-4">Orders</th>
                <th className="px-6 py-4">No. of Units</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Remark</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface text-text-primary">{tableBody}</tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-card border border-border bg-surface p-5 shadow-soft sm:grid-cols-3">
        <div className="flex flex-col">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-text-secondary">
            Total Units &bull; Amount
          </span>
          <span className="mt-1 text-lg font-black text-text-primary">
            {(data?.totalUnits || 0).toLocaleString('en-IN')} units
          </span>
          <span className="text-sm font-semibold text-text-secondary">{formatCurrency(data?.totalAmount || 0)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-text-secondary">COGS</span>
          <span className="mt-1 text-lg font-black text-text-primary">{formatCurrency(data?.cogs || 0)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-text-secondary">Gross Margin</span>
          <span className="mt-1 text-lg font-black text-status-active">{formatCurrency(grossMargin || 0)}</span>
        </div>
      </div>
    </SectionCard>
  )
}
